import { Component, Input, Output, OnChanges, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Validators } from '@angular/forms';
import { isNil } from 'lodash';
import { Result, TrackingService } from '@hub-components';
import { DEFAULT_IMAGE_SOURCE, UPLOAD_IMAGE, INSERT_URL, USE_ATTRIBUTE, LINK_URL_PATTERN } from '../../../../constants';
import { WidgetStyle, WidgetStyleOptions } from '../../interfaces/widget-style.interface';
import { ChartOptions } from '../chart-options';
import { prepareLinkUrl } from '../link-url.util';

@Component({
  selector: 'image-widget',
  templateUrl: 'image-widget.component.html',
  styleUrls: ['image-widget.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class ImageWidgetComponent implements OnChanges {
  @Input() result: Result;
  @Input() style: WidgetStyle;
  @Input() chartOptions: ChartOptions;
  @Input() active: boolean = false;
  @Input() editing: boolean = false;
  @Output() contentChanged: EventEmitter<any> = new EventEmitter();

  public imageUrl: string;
  public linkHref: string;
  public altText: string = '';
  public backgroundColor: string;

  private _imageSource: string = DEFAULT_IMAGE_SOURCE;
  private firstLoad: boolean = true;

  static supportedRollups = ['total'];
  static supportedOptions = [
    {
      bindTo: 'image',
      type: 'imageOptions',
      options: [
        {
          bindTo: 'imageSource',
        }, {
          bindTo: 'selectImageDimension',
        }, {
          bindTo: 'selectImageAttribute',
        }, {
          bindTo: 'imageUrl',
        },
      ],
    }, {
      bindTo: 'linkUrl',
      label: 'Link URL',
      type: 'string',
      tooltip: 'Opens this URL in a new tab when the image is clicked. Links are inactive while editing the dashboard.',
      validators: [Validators.pattern(LINK_URL_PATTERN)],
      invalidMessage: 'Make sure the URL is valid and has no spaces before or after it.',
    }, {
      bindTo: 'altText',
      label: 'Alt text',
      type: 'string',
      hideDivider: true,
      tooltip: 'Describes the image for screen readers. Recommended when the image has a link.',
    }, {
      bindTo: 'imageUrl',
      type: 'hidden',
    },
  ];

  static supportedStyle: WidgetStyleOptions[] = [
    {
      bindTo: 'backgroundColor',
      label: 'Background Color',
    },
    {
      bindTo: 'borderWidth',
      label: 'Thickness',
    },
    {
      bindTo: 'borderColor',
      label: 'Color',
    },
    {
      bindTo: 'shadow',
      label: 'Size',
    },
    {
      bindTo: 'shadowColor',
      label: 'Color',
    },
    {
      bindTo: 'shadowOpacity',
      label: 'Opacity',
    },
    {
      bindTo: 'borderRadius',
      label: 'Corner Radius',
    },
  ];


  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private trackingService: TrackingService,
  ) {}

  ngOnChanges() {
    if (this.style) {
      this.backgroundColor = this.style.backgroundColor;
    }
    if (this.chartOptions) {
      this.altText = this.chartOptions.altText ?? '';
      const linkUrl = this.chartOptions.linkUrl;

      if (this.editing || !LINK_URL_PATTERN.test(linkUrl)) {
        this.linkHref = null;
      } else {
        this.linkHref = prepareLinkUrl(linkUrl);
      }
      if (this.firstLoad) {
        this._imageSource = this.chartOptions.image && this.chartOptions.image.imageSource;
      }
      if (this.chartOptions.image?.imageSource === UPLOAD_IMAGE) {
        this._imageSource = UPLOAD_IMAGE;

        if (this.chartOptions.imageUrl) {
          this.imageUrl = this.chartOptions.imageUrl;
        }
      } else if (this.chartOptions.imageUrl || (this.chartOptions.image?.imageSource === INSERT_URL)) {
        this._imageSource = INSERT_URL;

        if (this.chartOptions.imageUrl) {
          this.imageUrl = this.chartOptions.imageUrl;
        }
      } else if (this.chartOptions.image) {
        const selectedAttribute = this.chartOptions.image.selectImageAttribute;
        const selectedDimension = this.chartOptions.image.selectImageDimension;
        if (this.chartOptions.image.imageSource === USE_ATTRIBUTE && selectedAttribute && this.result) {
          if (this.result.rows.length > 0) {
            const slice = this.result.rows[0].slices.find(slice => slice.dimension.name === selectedDimension.id);
            const attribute = slice?.properties?.find(prop => prop.name === selectedAttribute.id);
            if (!this.firstLoad && !isNil(this._imageSource) && this._imageSource !== this.chartOptions.image.imageSource) {
              this.trackingService.track('Image Widget: image source', { value: 'Use attribute' });
              this._imageSource = USE_ATTRIBUTE;
            }
            this.imageUrl = attribute && attribute.value;
          }
        }
      }
      this.changeDetectorRef.markForCheck();
    }
    this.firstLoad = false;
  }

  public trackLinkClick(): void {
    if (this.linkHref) {
      this.trackingService.track('Image Widget: Link clicked');
    }
  }
}
